import { useCallback } from "react";

import { useIntl } from "react-intl";

import { TGTGBagAdditionMethod } from "analytics/events";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { TranslatedProductWithPrice } from "shared/models/products/types";

import { BagQuantityUpdateResult, useTGTGBagStore } from "../stores/useTGTGBagStore";

const MAX_UNITS_PER_SKU_TOAST_ID = "max_units_per_sku_reached";

/**
 * Every path that puts units into a bag goes through these hooks, so the per-SKU
 * limit is enforced in the store and explained to the user in exactly one place.
 */
function useMaxUnitsPerSkuToast() {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const maximumUnitsPerSku = useTGTGBagStore((state) => state.maximumUnitsPerSku);

  return useCallback(() => {
    if (maximumUnitsPerSku === null) return;
    showToastUI({
      id: MAX_UNITS_PER_SKU_TOAST_ID,
      description: (
        <>
          {intl.formatMessage(
            { id: "pages.tgtg-bag-preparation-page.max-units-per-sku" },
            { count: maximumUnitsPerSku },
          )}
        </>
      ),
    });
  }, [intl, maximumUnitsPerSku, showToastUI]);
}

export function useAddProductToBag() {
  const addProduct = useTGTGBagStore((state) => state.addProduct);
  const { sendSegmentTrackEvent } = useAnalytics();
  const showMaxUnitsPerSkuToast = useMaxUnitsPerSkuToast();

  return useCallback(
    (
      product: TranslatedProductWithPrice,
      method: TGTGBagAdditionMethod,
    ): BagQuantityUpdateResult => {
      const result = addProduct(product, method, sendSegmentTrackEvent);
      if (result === "limit-reached") {
        showMaxUnitsPerSkuToast();
      }
      return result;
    },
    [addProduct, sendSegmentTrackEvent, showMaxUnitsPerSkuToast],
  );
}

export function useIncreaseProductQuantity() {
  const increaseQuantity = useTGTGBagStore((state) => state.increaseQuantity);
  const { sendSegmentTrackEvent } = useAnalytics();
  const showMaxUnitsPerSkuToast = useMaxUnitsPerSkuToast();

  return useCallback(
    (sku: string): BagQuantityUpdateResult => {
      const result = increaseQuantity(sku, sendSegmentTrackEvent);
      if (result === "limit-reached") {
        showMaxUnitsPerSkuToast();
      }
      return result;
    },
    [increaseQuantity, sendSegmentTrackEvent, showMaxUnitsPerSkuToast],
  );
}
